import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { UpstreamMember } from '@/types/originGroup';

export interface UpstreamMembersEditorProps {
  members: UpstreamMember[];
  onChange: (members: UpstreamMember[]) => void;
  /** Port used for newly added servers */
  defaultPort: number;
  error?: string;
}

/**
 * Create an upstream member with nginx defaults
 */
function createUpstreamMember(port: number): UpstreamMember {
  return {
    address: '',
    port,
    weight: 1,
    max_fails: 1,
    fail_timeout: 10,
    backup: false,
    down: false,
  };
}

/**
 * Editor for the load-balanced upstream servers of an origin group
 */
export function UpstreamMembersEditor({ members, onChange, defaultPort, error }: UpstreamMembersEditorProps) {
  const updateMember = (index: number, patch: Partial<UpstreamMember>) => {
    onChange(members.map((member, i) => (i === index ? { ...member, ...patch } : member)));
  };

  const removeMember = (index: number) => {
    onChange(members.filter((_, i) => i !== index));
  };

  return (
    <div className="origin-group-modal-section">
      <p className="origin-group-modal-help">
        Configure multiple upstream servers for load balancing. Leave empty to use the single address above.
      </p>

      {error && <div className="origin-group-modal-error">{error}</div>}

      <div className="upstream-members-list">
        {members.map((member, index) => (
          <div key={index} className="upstream-member-item">
            <div className="upstream-member-header">
              <span className="upstream-member-number">Server {index + 1}</span>
              <button
                type="button"
                className="upstream-member-remove"
                onClick={() => removeMember(index)}
                aria-label={`Remove server ${index + 1}`}
              >
                Remove
              </button>
            </div>
            <div className="upstream-member-row">
              <Input
                label="Address"
                value={member.address}
                onChange={(e) => updateMember(index, { address: e.target.value })}
                placeholder="hostname or IP"
                required
              />
              <Input
                label="Port"
                type="number"
                value={member.port}
                onChange={(e) => updateMember(index, { port: parseInt(e.target.value) || 0 })}
                style={{ maxWidth: '80px' }}
              />
              <Input
                label="Weight"
                type="number"
                value={member.weight || 1}
                onChange={(e) => updateMember(index, { weight: parseInt(e.target.value) || 1 })}
                style={{ maxWidth: '70px' }}
              />
            </div>
            <div className="upstream-member-row">
              <Input
                label="Max Fails"
                type="number"
                value={member.max_fails || 1}
                onChange={(e) => updateMember(index, { max_fails: parseInt(e.target.value) || 1 })}
                style={{ maxWidth: '90px' }}
              />
              <Input
                label="Fail Timeout (s)"
                type="number"
                value={member.fail_timeout || 10}
                onChange={(e) => updateMember(index, { fail_timeout: parseInt(e.target.value) || 10 })}
                style={{ maxWidth: '110px' }}
              />
              <label className="upstream-member-checkbox">
                <input
                  type="checkbox"
                  checked={member.backup || false}
                  onChange={(e) => updateMember(index, { backup: e.target.checked })}
                />
                <span>Backup</span>
              </label>
              <label className="upstream-member-checkbox">
                <input
                  type="checkbox"
                  checked={member.down || false}
                  onChange={(e) => updateMember(index, { down: e.target.checked })}
                />
                <span>Down</span>
              </label>
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="secondary"
        onClick={() => onChange([...members, createUpstreamMember(defaultPort)])}
      >
        Add Server
      </Button>
    </div>
  );
}
